import { useState, useEffect } from "react";
import { Trash2, PlusCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_API_URL || "";

async function getReglements() {
  const r = await axios.get(`${API}/api/reglements-differes`);
  return r.data;
}
async function createReglement(data) {
  const r = await axios.post(`${API}/api/reglements-differes`, data);
  return r.data;
}
async function deleteReglement(id) {
  await axios.delete(`${API}/api/reglements-differes/${id}`);
}

export default function ReglementsPage() {
  const [reglements, setReglements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    client_nom: "",
    numero_facture: "",
    montant: "",
    mode_paiement: "cheque",
    remarque: "",
  });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const data = await getReglements();
      setReglements(data);
    } catch {
      toast.error("Erreur chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.client_nom || !form.numero_facture || !form.montant) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    setSaving(true);
    try {
      await createReglement({ ...form, montant: parseFloat(form.montant) });
      toast.success("Règlement enregistré !");
      setForm({ date: new Date().toISOString().slice(0, 10), client_nom: "", numero_facture: "", montant: "", mode_paiement: "cheque", remarque: "" });
      load();
    } catch {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce règlement ?")) return;
    try {
      await deleteReglement(id);
      toast.success("Supprimé");
      load();
    } catch {
      toast.error("Erreur suppression");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Règlements différés</h1>
        <p className="text-slate-500 text-sm mt-1">Encaissement d'une facture antérieure (Henrri ou autre)</p>
      </div>

      {/* Formulaire */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <h2 className="font-semibold text-slate-800 mb-4">Nouveau règlement</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Date du règlement *</Label>
            <Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} />
          </div>
          <div>
            <Label>Nom du client *</Label>
            <Input placeholder="Ex : DUPONT Jean" value={form.client_nom} onChange={e => setForm({...form, client_nom: e.target.value})} />
          </div>
          <div>
            <Label>N° de facture *</Label>
            <Input placeholder="Ex : FA-2026-0042" value={form.numero_facture} onChange={e => setForm({...form, numero_facture: e.target.value})} />
          </div>
          <div>
            <Label>Montant TTC (€) *</Label>
            <Input type="number" step="0.01" placeholder="Ex : 150.00" value={form.montant} onChange={e => setForm({...form, montant: e.target.value})} />
          </div>
          <div>
            <Label>Mode de paiement *</Label>
            <select
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm"
              value={form.mode_paiement}
              onChange={e => setForm({...form, mode_paiement: e.target.value})}
            >
              <option value="cheque">Chèque</option>
              <option value="cb">Carte bancaire</option>
            </select>
          </div>
          <div>
            <Label>Remarque (optionnel)</Label>
            <Input placeholder="Ex : règlement suite devis" value={form.remarque} onChange={e => setForm({...form, remarque: e.target.value})} />
          </div>
        </div>
        <Button className="mt-4 bg-[#84CC16] hover:bg-[#65a30d] text-white" onClick={handleSubmit} disabled={saving}>
          <PlusCircle className="w-4 h-4 mr-2" />
          {saving ? "Enregistrement..." : "Enregistrer le règlement"}
        </Button>
      </div>

      {/* Liste */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <h2 className="font-semibold text-slate-800 mb-4">Historique</h2>
        {loading ? (
          <p className="text-slate-400 text-sm">Chargement...</p>
        ) : reglements.length === 0 ? (
          <p className="text-slate-400 text-sm">Aucun règlement différé enregistré.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b">
                <th className="pb-2">Date</th>
                <th className="pb-2">Client</th>
                <th className="pb-2">N° Facture</th>
                <th className="pb-2">Montant</th>
                <th className="pb-2">Mode</th>
                <th className="pb-2">Remarque</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {reglements.map(r => (
                <tr key={r.id} className="border-b last:border-0 hover:bg-slate-50">
                  <td className="py-2">{r.date}</td>
                  <td className="py-2 font-medium">{r.client_nom}</td>
                  <td className="py-2">{r.numero_facture}</td>
                  <td className="py-2 font-semibold">{Number(r.montant).toFixed(2)} €</td>
                  <td className="py-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.mode_paiement === "cheque" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"}`}>
                      {r.mode_paiement === "cheque" ? "Chèque" : "CB"}
                    </span>
                  </td>
                  <td className="py-2 text-slate-500">{r.remarque}</td>
                  <td className="py-2">
                    <button onClick={() => handleDelete(r.id)} className="text-red-400 hover:text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
